import React from "react";
import { useTranslation } from "react-i18next";
import useRevealSection from "../hooks/useRevealSection";
import useLangPrefix from "../hooks/useLangPrefix";
import promptdooScreenshot from "../asset/promptdoo-screenshot.png";
import portfolioScreenshot from "../asset/portfolio-screenshot.png";

const Projects: React.FC = () => {
  const { t } = useTranslation();
  const prefix = useLangPrefix();
  const sectionRef = useRevealSection<HTMLElement>();

  return (
    <section className="project" id="projects" ref={sectionRef}>
      <h2 className="heading reveal">
        {t("projects.headingPre")} <span>{t("projects.headingSpan")}</span>
      </h2>

      <div className="project-row">
        <div className="project-column">
          <h3 className="title">{t("projects.ownTitle")}</h3>

          <div className="project-box stagger">
            <div className="project-content reveal">
              <div className="content">
                <img
                  src={promptdooScreenshot}
                  alt="Screenshot von promptdoo.com"
                  className="project-screenshot"
                />
                <div className="year">
                  <i className="bx bxs-calendar"></i> 2026
                </div>
                <h3>
                  {t("projects.promptdoo.titlePrefix")}{" "}
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href="https://promptdoo.com"
                  >
                    promptdoo.com
                  </a>
                </h3>
                <p>
                  <strong>{t("projects.promptdoo.descLabel")}</strong>:{" "}
                  {t("projects.promptdoo.desc")}
                </p>
                <p>
                  <strong>{t("projects.promptdoo.statusLabel")}</strong>:{" "}
                  {t("projects.promptdoo.status")}
                </p>
              </div>
            </div>

            <div className="project-content reveal">
              <div className="content">
                <img
                  src="/images/income-clock/icon.png"
                  alt="Income Clock App-Icon"
                  className="project-app-icon"
                  width={72}
                  height={72}
                />
                <div className="year">
                  <i className="bx bxs-calendar"></i>{" "}
                  {t("projects.incomeClock.year")}
                </div>
                <h3>
                  <a href={`${prefix}/projekte/income-clock`}>Income Clock</a>
                </h3>
                <p>
                  <strong>{t("projects.incomeClock.descLabel")}</strong>:{" "}
                  {t("projects.incomeClock.desc")}
                </p>
                <p>
                  <strong>{t("projects.incomeClock.platformsLabel")}</strong>:{" "}
                  {t("projects.incomeClock.platforms")}
                </p>
                <p>
                  <strong>{t("projects.incomeClock.statusLabel")}</strong>:{" "}
                  {t("projects.incomeClock.status")}
                </p>
                <p>
                  <a
                    className="project-more"
                    href={`${prefix}/projekte/income-clock`}
                  >
                    {t("projects.incomeClock.more")}{" "}
                    <i className="bx bx-right-arrow-alt"></i>
                  </a>
                </p>
              </div>
            </div>

            <div className="project-content reveal">
              <div className="content">
                <img
                  src="/images/neverlull/icon.svg"
                  alt="Neverlull App-Icon"
                  className="project-app-icon"
                  width={72}
                  height={72}
                />
                <div className="year">
                  <i className="bx bxs-calendar"></i>{" "}
                  {t("projects.neverlull.year")}
                </div>
                <h3>
                  <a target="_blank" rel="noreferrer" href="https://neverlull.com">
                    Neverlull
                  </a>
                </h3>
                <p>
                  <strong>{t("projects.neverlull.descLabel")}</strong>:{" "}
                  {t("projects.neverlull.desc")}
                </p>
                <p>
                  <strong>{t("projects.neverlull.platformsLabel")}</strong>:{" "}
                  {t("projects.neverlull.platforms")}
                </p>
                <p>
                  <strong>{t("projects.neverlull.statusLabel")}</strong>:{" "}
                  {t("projects.neverlull.status")}
                </p>
                <p>
                  <a
                    className="project-more"
                    target="_blank"
                    rel="noreferrer"
                    href="https://neverlull.com"
                  >
                    {t("projects.neverlull.more")}{" "}
                    <i className="bx bx-link-external"></i>
                  </a>
                  {" · "}
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href="https://neverlull.com/datenschutz"
                  >
                    {t("projects.neverlull.privacy")}
                  </a>
                </p>
              </div>
            </div>

            <div className="project-content reveal">
              <div className="content">
                <div className="year">
                  <i className="bx bxs-calendar"></i> {t("projects.ios.year")}
                </div>
                <h3>{t("projects.ios.title")}</h3>
                <p>
                  <strong>{t("projects.ios.descLabel")}</strong>:{" "}
                  {t("projects.ios.desc")}
                </p>
                <p>
                  <strong>{t("projects.ios.statusLabel")}</strong>:{" "}
                  {t("projects.ios.status")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="project-column">
          <h3 className="title">{t("projects.otherTitle")}</h3>

          <div className="project-box stagger">
            <div className="project-content reveal">
              <div className="content">
                <img
                  src={portfolioScreenshot}
                  alt="Screenshot von grimmenstein.dev"
                  className="project-screenshot"
                />
                <div className="year">
                  <i className="bx bxs-calendar"></i> 2024
                </div>
                <h3>
                  {t("projects.portfolio.titlePrefix")}{" "}
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href="https://grimmenstein.dev"
                  >
                    Grimmenstein.dev
                  </a>
                </h3>
                <p>
                  <strong>{t("projects.portfolio.descLabel")}</strong>:{" "}
                  {t("projects.portfolio.desc")}
                </p>
                <p>
                  <strong>{t("projects.portfolio.techLabel")}</strong>: React,
                  TypeScript, JavaScript, PHP, HTML, CSS
                </p>
              </div>
            </div>

            <div className="project-content reveal">
              <div className="content">
                <div className="year">
                  <i className="bx bxs-calendar"></i> 2012-2014
                </div>
                <h3>
                  {t("projects.stadoo.titlePrefix")}{" "}
                  <a href="https://stadoo.com">Stadoo.com</a>
                </h3>
                <p>
                  <strong>{t("projects.stadoo.descLabel")}</strong>:{" "}
                  {t("projects.stadoo.desc")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
